<template>
  <div class="container checkout">
    <span class="checkout__title mb-30">Checkout</span>
    <div class="d-flex">
      <div class="col-lg-6 checkout__panel">
        <template v-if="product.id">
          <div class="d-flex checkout__panel-holder mb-30">
            <a href="#" @click="handleBack($event)" class="checkout__link">
              <img src="@/assets/media/icons/back_arrow.svg" alt="back arrow" />
            </a>
            <img
              src="@/assets/media/brand.svg"
              alt="brand logo"
              class="checkout__brand"
            />
            <div v-if="plan.livemode === false">
              <span class="badge badge-warning">TEST</span>
            </div>
          </div>
          <span class="checkout__panel-description">
            {{ product.name }}
          </span>
          <span class="checkout__panel-price">
            <span class="mr-1">${{ plan.amount_conv }}.00</span>
            <div class="d-flex flex-column">
              <span>per</span>
              <span>{{ plan.interval }}</span>
            </div>
          </span>
          <div
            class="mt-auto align-items-center panel-footer d-none"
            style="font-size: 12px;"
          >
            <span>Powered by</span>
            <img
              src="@/assets/media/stripe-logo.png"
              alt="Stripe Logo"
              style="width: 40px;"
              class="mr-3"
            />
          </div>
        </template>
        <Spinner v-else :theme="primary" />
      </div>
      <div class="col-lg-6 checkout__panel">
        <h4>Pay with card</h4>
        <form class="credit-card-inputs" @submit.prevent="handlePay($event)">
          <div class="form-group">
            <label>Email</label>
            <input
              type="email"
              class="form-control"
              v-model.trim="formData.email"
              data-vv-as="email"
              name="email"
              v-validate="
                'required|email|regex:^([a-z\\d]+[-_\.]?)+@([^\s@])+\.([^\s@])+$'
              "
            />
            <div v-if="veeErrors.first('email')" class="invalid-feedback">
              {{ veeErrors.first("email") }}
            </div>
          </div>
          <div class="form-group">
            <label>Card Information</label>
            <div class="form-group-custom">
              <div
                id="card-number"
                class="form-control"
                :class="!card.number && stripeError != '' ? 'is-invalid' : ''"
                style="padding: 13px 14px;"
              ></div>
              <div class="form-group-chaser">
                <div
                  id="card-expiry"
                  class="form-control"
                  :class="!card.expiry && stripeError != '' ? 'is-invalid' : ''"
                  style="padding: 13px 14px;"
                ></div>
                <div
                  id="card-cvc"
                  class="form-control"
                  :class="!card.cvc && stripeError != '' ? 'is-invalid' : ''"
                  style="padding: 13px 14px;"
                ></div>
              </div>
            </div>
            <div v-if="stripeError != ''" class="invalid-feedback-custom">
              {{ stripeError }}
            </div>
          </div>
          <div class="form-group">
            <label>Name on card</label>
            <input
              type="text"
              name="name"
              class="form-control"
              v-model.trim="formData.nameOnCard"
              data-vv-as="name"
              v-validate="'required|regex:^[A-Za-z\\s\\d-\.]*$'"
            />
            <div v-if="veeErrors.first('name')" class="invalid-feedback">
              {{ veeErrors.first("name") }}
            </div>
          </div>
          <div class="form-group">
            <label>Country or region</label>
            <CustomSelect
              :options="countries"
              :placeholder="'Select Country'"
              v-model="formData.countries"
              :searchable="true"
              @typing="handleType"
              style="height: 42px;"
              :config="{
                displaySize: 4
              }"
              form_type="form_type default"
            />
          </div>
          <button class="btn" style="height: 42px;" :disabled="loader">
            Subscribe
          </button>
          <div class="card small mt-4 text-center">
            By confirming your subscription, you allow Oqulo to charge your card
            for this payment and future payments in accordance with their
            <a
              href="https://www.qspaceapp.com/terms-of-service/"
              target="_blank"
              :style="`color: ${primary}`"
              >terms</a
            >.
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "@/helpers/axios";
import stripe from "@/helpers/payments/stripe";
import stripeKey from "@/helpers/payments/stripeKey";

const Spinner = () => import("@/components/Spinner");
const CustomSelect = () => import("@/components/Select");

export default {
  components: {
    Spinner,
    CustomSelect
  },
  data() {
    return {
      stripe: stripeKey,
      plan: {},
      product: {},
      countries: [],
      storeCountries: [],
      formData: {
        email: null,
        plan: null,
        nameOnCard: null,
        countries: null,
        token: null,
        amount: 0,
        currency: "ph",
        tenant: ""
      },
      cardNumber: "",
      cardExpiry: "",
      cardCvc: "",
      card: {
        cvc: "",
        number: "",
        expiry: ""
      },
      stripeError: "",
      cardCvcError: "",
      cardExpiryError: "",
      cardNumberError: "",
      cardStyle: {
        base: {
          fontSize: "13px",
          color: "#343a40",
          fontFamily: "Rubik, sans-serif",
          fontSmoothing: "antialiased",
          "::placeholder": {
            color: "#848484"
          }
        },
        invalid: {
          iconColor: "#ef5350",
          color: "#ef5350"
        }
      },
      loader: false
    };
  },
  created() {
    this.init();
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    }
  },
  mounted() {
    this.setUpStripe();
  },
  methods: {
    ...mapActions({
      SET_STRIPE_CHECKOUT: "SET_STRIPE_CHECKOUT"
    }),
    ...mapGetters({
      getStripeCheckout: "getStripeCheckout"
    }),
    setUpStripe() {
      const elements = stripe.elements();
      this.cardCvc = elements.create("cardCvc", { style: this.cardStyle });
      this.cardExpiry = elements.create("cardExpiry", {
        style: this.cardStyle
      });
      this.cardNumber = elements.create("cardNumber", {
        style: this.cardStyle
      });

      this.cardCvc.mount("#card-cvc");
      this.cardExpiry.mount("#card-expiry");
      this.cardNumber.mount("#card-number");

      this.listenForErrors();
    },
    listenForErrors() {
      this.cardNumber.addEventListener("change", event => {
        this.toggleError(event);
        this.cardNumberError = "";
        this.card.number = event.complete ? true : false;
      });

      this.cardExpiry.addEventListener("change", event => {
        this.toggleError(event);
        this.cardExpiryError = "";
        this.card.expiry = event.complete ? true : false;
      });

      this.cardCvc.addEventListener("change", event => {
        this.toggleError(event);
        this.cardCvcError = "";
        this.card.cvc = event.complete ? true : false;
      });
    },
    toggleError(event) {
      if (event.error) {
        this.stripeError = event.error.message;
      } else {
        this.stripeError = "";
      }
      this.loader = this.stripeError == "" ? false : true;
    },
    handleBack: function(evt) {
      evt.preventDefault();
      this.SET_STRIPE_CHECKOUT(null);
    },
    async init() {
      await this.handleGetCountries();
      this.handleGetItem();
    },
    async handleGetCountries() {
      this.countries = await axios.get("/locations/countries").then(result => {
        return result.data.data;
      });
      this.storeCountries = this.countries;
    },
    async handleGetItem() {
      this.plan = this.getStripeCheckout();
      this.product = await axios
        .get(`/stripe/${this.plan.product}`)
        .then(result => {
          return result.data.data;
        });
    },
    handleType() {
      this.countries = this.storeCountries.filter(item => {
        let find = event.target.value.toLowerCase();
        return item.label.toLowerCase().includes(find);
      });
    },
    async handlePay() {
      this.formData.amount = this.plan.amount;
      this.formData.currency = this.plan.currency;
      this.formData.plan = this.plan.id;
      this.formData.tenant = window.location.host.split(".")[0];

      this.loader = true;
      try {
        const isFormValid = await this.$validator.validateAll();
        if (!this.card.number || !this.card.cvc || !this.card.expiry) {
          this.stripeError = "Card Information is Required";
        }
        if (isFormValid) {
          await stripe.createToken(this.cardNumber).then(async tokenResult => {
            await axios
              .post("/stripe/checkout", {
                token: tokenResult,
                form: this.formData
              })
              .then(result => {
                if (result.data.subscriptions.status == "active") {
                  this.$swal({
                    text: `Your ${this.plan.nickname.toLowerCase()} subscription has been activated`,
                    title: "Thank You!",
                    type: "success"
                  });
                  this.SET_STRIPE_CHECKOUT(null);
                } else {
                  this.$swal({
                    text: `Please try again later.`,
                    title: "OOPS!",
                    type: "error"
                  });
                }
              });
          });
        }
      } catch (error) {
        const errType = error.type || "error";
        const errTitle = error.title || "Error";
        this.$toastr[errType]("Please try again.", errTitle);
        this.loader = false;
      }
    }
  }
};
</script>
<style lang="scss" scope>
.checkout {
  font-family: "Gotham" !important;
  &__panel {
    padding-top: 40px;
    padding-bottom: 40px;
    &:first-child {
      display: flex;
      flex-direction: column;
    }
    &:last-child {
      box-shadow: -7px 0 7px -6px rgba(0, 0, 0, 0.1);
      background-color: #fff;
      padding-left: 70px;
      padding-right: 70px;
    }
    &-holder {
      align-items: center;
      justify-content: space-between;
      width: 150px;
    }
    &-description {
      font-size: 18px;
      font-weight: 500;
    }
    &-price {
      font-size: 42px;
      font-weight: 500;
      display: flex;
      .flex-column {
        justify-content: center;
        span {
          font-size: 14px;
          line-height: 1.3;
        }
      }
    }
    .panel-footer {
      &-link {
        color: #888888 !important;
        position: relative;
        &:nth-child(3)::before {
          color: #777777;
          content: "|";
          position: absolute;
          right: 45px;
        }
      }
      img {
        opacity: 0.5;
      }
    }
  }
  &__title {
    font-size: 20px;
    display: block;
  }
  &__link {
    color: #cccccc;
    img {
      width: 17px;
      opacity: 0.6;
    }
  }
  &__brand {
    width: 70px;
  }
  .form-group-custom {
    display: flex;
    flex-direction: column;
    position: relative;
    > img {
      position: absolute;
      width: 34px;
      right: 10px;
      top: 10px;
      height: 23px;
    }
    > .form-control {
      border-radius: 0.25rem 0.25rem 0 0;
    }
    .form-group-chaser {
      display: flex;
      position: relative;
      > img {
        position: absolute;
        width: 31px;
        right: 10px;
        top: 10px;
        height: 24px;
        opacity: 0.5;
      }
      > .form-control {
        border-radius: 0 0 0.25rem 0.25rem;
        border-top: 0px;
        &:nth-child(1) {
          border-radius: 0 0 0 0.25rem;
          border-right: 0;
        }
        &:nth-child(2) {
          border-radius: 0 0 0.25rem 0;
        }
      }
    }
  }
  .credit-card-inputs {
    .complete {
      border: 2px solid green;
    }
    .form-control {
      height: 42px;
    }
    label {
      font-weight: 500;
      font-size: 14px;
    }
    .btn {
      background-color: #383dd8;
      color: #fff;
      width: 100%;
      opacity: 1;
      &:hover {
        background-color: #2226a5;
      }
      &:disabled {
        background-color: #2226a5;
        opacity: 0.5;
      }
    }
    .invalid-feedback {
      font-size: 12px;
    }
    .invalid-feedback-custom {
      margin-top: 0.25rem;
      font-size: 12px;
      color: #ef5350;
    }
  }
  span {
    font-family: "Gotham" !important;
  }
}
</style>
